import { useCallback, useEffect, useMemo } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation } from '@apollo/client';
import { z } from 'zod';

import { TooltipTrigger } from 'react-aria-components';
import SectionTitle from 'src/components/ui/SectionTitle';
import BackButton from 'src/components/BackButton';
import IconButton from 'src/components/ui/IconButton';
import PageLoader from 'src/components/ui/PageLoader';
import MaterialReader from 'src/components/material/reader/MaterialReader';
import MaterialMedia from 'src/components/material/MaterialMedia';
import MaterialAiBar from 'src/components/material/MaterialAiBar';
import Button from 'src/components/ui/Button';
import EmptyState from 'src/components/ui/EmptyState';
import { Tooltip } from 'src/components/ui/Tooltip';
import LeftIcon from 'src/assets/icons/direction-left.svg';
import RightIcon from 'src/assets/icons/direction-right.svg';
import OPEN_COURSE_MUTATION from 'src/graphql/mutation/open-course.graphql';
import {
  CourseMaterial,
  Media,
  SemanticSearch,
  Summary,
} from 'src/types/course';
import { DateTime } from 'luxon';

const CourseIndexPage = () => {
  const { courseId } = Route.useParams();
  const { materialId } = Route.useSearch();
  const navigate = Route.useNavigate();

  const [openCourse, { data, loading, error, called }] = useMutation(
    OPEN_COURSE_MUTATION,
    {
      variables: { id: courseId },
    }
  );

  const { materials, aiSettings } = useMemo(() => {
    const course = data?.courseOpen ?? {};

    return {
      materials: course.courseMaterials || [],
      aiSettings: {
        enabled: Boolean(course.enableAi),
        semanticSearch: course.semanticSearch as SemanticSearch,
        summary: course.summary as Summary,
      },
    };
  }, [data?.courseOpen]);

  const { currentMaterial, currentMaterialIndex, currentMaterialAvailable } =
    useMemo(() => {
      const current: CourseMaterial = materials.find(
        (m: CourseMaterial) => Number(m.courseMaterialId) === Number(materialId)
      );

      const currentIndex: number = materials.indexOf(current);

      let isAvailable = true;

      if (current?.startDate && DateTime.fromISO(current.startDate).isValid) {
        const startDate = DateTime.fromISO(current.startDate);
        const startSecondsLeft = startDate?.diffNow('seconds')?.seconds;

        isAvailable = startSecondsLeft <= 0;
      }

      return {
        currentMaterial: current,
        currentMaterialIndex: currentIndex,
        currentMaterialAvailable: isAvailable,
      };
    }, [materials, materialId]);

  const onSearchUpdate = useCallback(
    async (search: ResourceSchemaType) => {
      await navigate({
        search: (old) => {
          return {
            ...old,
            ...search,
          };
        },
        replace: true,
      });
    },
    [navigate]
  );

  const onPrev = useCallback(async () => {
    if (currentMaterialIndex < 1) return;

    const search = {
      materialId: Number(materials[currentMaterialIndex - 1].courseMaterialId),
    };

    await onSearchUpdate(search);
  }, [currentMaterialIndex, materials, onSearchUpdate]);

  const onNext = useCallback(async () => {
    const nextId = currentMaterialIndex + 1;

    if (nextId >= materials.length) return;

    const search: ResourceSchemaType = {
      materialId: Number(materials[nextId].courseMaterialId),
    };

    await onSearchUpdate(search);
  }, [currentMaterialIndex, materials, onSearchUpdate]);

  useEffect(() => {
    const abortController = new AbortController();

    // Suppress the global GraphQL error toast; we render an inline recovery
    // state below (see Issue #494) so a toast would duplicate the message.
    const callback = async () =>
      openCourse({
        context: {
          fetchOptions: { signal: abortController.signal },
          notifyOnError: false,
        },
      });

    callback().catch(() => {});

    return () => abortController.abort();
  }, [openCourse]);

  if (loading || !called) return <PageLoader />;
  if (error || !currentMaterial?.courseMaterialId) {
    const message = error
      ? error.message
      : 'The requested material is not part of this course';
    return (
      <div className="flex flex-col h-screen px-2 sm:px-4 lg:px-8 pb-5">
        <div className="section-title flex flex-col items-center gap-2 px-6 py-4 xs:flex-row">
          <BackButton to="/dashboard" />
        </div>
        <div className="flex-1 min-h-0 flex items-center justify-center bg-surface-alt rounded-3xl shadow-3">
          <EmptyState message={message}>
            <Button onPress={() => navigate({ to: '/dashboard' })}>
              Back to Dashboard
            </Button>
          </EmptyState>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 lg:px-8">
      <div className="flex flex-col relative overflow-hidden h-screen items-stretch mx-auto pb-5">
        <>
          <div className="section-title flex flex-col items-center gap-2 px-6 py-4 xs:flex-row">
            <BackButton to="/dashboard" />
            <SectionTitle
              title={currentMaterial.name}
              titleClassName="line-clamp-2"
              className="p-0"
            />
            <div className="flex items-center gap-2 xs:ml-auto">
              <TooltipTrigger delay={0}>
                <IconButton
                  size="small"
                  Icon={LeftIcon}
                  onPress={onPrev}
                  aria-label="Previous material"
                  isDisabled={currentMaterialIndex < 1}
                  className="bg-surface-alt-ghost hover:bg-surface-alt-ghost-hover"
                />
                <Tooltip>Previous Material</Tooltip>
              </TooltipTrigger>
              <TooltipTrigger delay={0}>
                <IconButton
                  size="small"
                  Icon={RightIcon}
                  onPress={onNext}
                  aria-label="Next material"
                  isDisabled={currentMaterialIndex + 1 >= materials.length}
                  className="bg-surface-alt-ghost hover:bg-surface-alt-ghost-hover"
                />
                <Tooltip>Next Material</Tooltip>
              </TooltipTrigger>
            </div>
          </div>
          {currentMaterialAvailable ? (
            <>
              {currentMaterial.media !== Media.PDF && (
                <>
                  <MaterialAiBar
                    courseId={courseId}
                    material={currentMaterial}
                    aiSettings={aiSettings}
                  />
                  <MaterialMedia
                    courseId={courseId}
                    material={currentMaterial}
                  />
                </>
              )}
              {currentMaterial.media === Media.PDF && (
                <MaterialReader
                  // Previous/Next stay on this route; a fresh reader per
                  // material keeps one PDF's state out of the next (#528).
                  key={currentMaterial.courseMaterialId}
                  courseId={courseId}
                  material={currentMaterial}
                  aiSettings={aiSettings}
                  downloadsAllowed={Boolean(data?.courseOpen?.downloadsAllowed)}
                />
              )}
            </>
          ) : (
            <EmptyState
              message={`This material will be available ${DateTime.fromISO(currentMaterial.startDate!).toRelative()}.`}
              className="text-center px-10 flex-1"
            />
          )}
        </>
      </div>
    </div>
  );
};

const ResourceSchema = z.object({ materialId: z.number() });

type ResourceSchemaType = z.infer<typeof ResourceSchema>;

export const Route = createFileRoute('/_authenticated/courses/$courseId')({
  component: CourseIndexPage,
  validateSearch: (search) => ResourceSchema.parse(search),
  beforeLoad: async ({ search }) => {
    const { success } = ResourceSchema.safeParse(search);

    if (!success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/dashboard',
      });
    }
  },
});
